import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  TextField,
  IconButton,
  Paper,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import MinimizeIcon from '@mui/icons-material/Minimize';
import DeleteIcon from '@mui/icons-material/Delete';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import ReactMarkdown from 'react-markdown';
import remarkBreaks from 'remark-breaks';
import { v4 as uuidv4 } from 'uuid';
import { useApi, identityApiRef } from '@backstage/core-plugin-api';

// Function to check and clear old chat history
const checkAndClearOldHistory = () => {
  const lastClearDate = localStorage.getItem("lastClearDate");
  const today = new Date().toDateString();
  
  if (lastClearDate !== today) {
    localStorage.removeItem("askGPTChatHistory");
    localStorage.setItem("lastClearDate", today);
    return true; // History was cleared
  }
  return false; // History was not cleared
};

const AskGPTChatbox: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [isLarge, setIsLarge] = useState(false);
  const [message, setMessage] = useState("");
  const [chatLog, setChatLog] = useState<string[]>(() => {
    // Check and clear old history first
    const wasCleared = checkAndClearOldHistory();
    if (wasCleared) {
      return [];
    }
    
    const stored = localStorage.getItem("askGPTChatHistory");
    return stored ? JSON.parse(stored) : [];
  });
  const [loading, setLoading] = useState(false);
  const [disclaimerAccepted, setDisclaimerAccepted] = useState<boolean>(() => {
    return localStorage.getItem("disclaimerAccepted") === "true";
  });
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  const identityApi = useApi(identityApiRef);

  const chatContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    localStorage.setItem("askGPTChatHistory", JSON.stringify(chatLog));
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [chatLog]);

  useEffect(() => {
    if (open && chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [open]);

  const [sessionId] = useState<string>(() => {
    const stored = localStorage.getItem("sessionId");
    if (stored) return stored;
    const newId = uuidv4();
    localStorage.setItem("sessionId", newId);
    return newId;
  });

  const toggleOpen = () => {
    if (!disclaimerAccepted) {
      setShowDisclaimer(true);
    } else {
      setOpen(!open);
    }
  };

  const handleDisclaimerAgree = () => {
    setDisclaimerAccepted(true);
    localStorage.setItem("disclaimerAccepted", "true");
    setShowDisclaimer(false);
    setOpen(true);
  };

  const handleDisclaimerCancel = () => {
    setShowDisclaimer(false);
  };

  const toggleResize = () => {
    setIsLarge(!isLarge);
  };

  const handleClear = () => {
    setChatLog([]);
  };

  const handleSubmit = async () => {
    if (!message.trim()) return;
    
    const currentMessage = message;
    setChatLog(prev => [...prev, `You: ${currentMessage}`]);
    setMessage("");
    setLoading(true);
    
    try {
      // Get the user identity information
      const { token } = await identityApi.getCredentials();
      
      const response = await fetch("/api/proxy/gpt/query", {
        method: "POST",
        headers: { 
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
        },
        credentials: 'include', // Include cookies and auth headers
        body: JSON.stringify({ session_id: sessionId, question: currentMessage }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Server responded with ${response.status}: ${errorText}`);
      }
      
      const text = await response.text();
      if (!text) {
        throw new Error("Empty response body");
      }
      const data = JSON.parse(text);
      
      setChatLog(prev => [...prev, `GPT: ${data.answer}`]);
    } catch (error: any) {
      console.error("Error:", error);
      setChatLog(prev => [...prev, `Error: ${error.toString()}`]);
    }
    
    setLoading(false);
  };

  const renderEntry = (entry: string, idx: number) => {
    const markdownProps = { remarkPlugins: [remarkBreaks] };
    if (entry.startsWith("You:")) {
      const content = entry.slice(4).trim();
      return (
        <Typography key={idx} variant="body2" sx={{ mb: 0.5 }}>
          <span style={{ color: "yellow", fontWeight: "bold" }}>You:</span>{" "}
          <ReactMarkdown {...markdownProps}>{content}</ReactMarkdown>
        </Typography>
      );
    } else if (entry.startsWith("GPT:")) {
      const content = entry.slice(4).trim();
      return (
        <Typography key={idx} variant="body2" sx={{ mb: 0.5 }}>
          <span style={{ color: "red", fontWeight: "bold" }}>GPT:</span>{" "}
          <ReactMarkdown {...markdownProps}>{content}</ReactMarkdown>
        </Typography>
      );
    } else {
      return (
        <Typography key={idx} variant="body2" sx={{ mb: 0.5 }}>
          <ReactMarkdown {...markdownProps}>{entry}</ReactMarkdown>
        </Typography>
      );
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <>
      {/* Disclaimer Dialog */}
      <Dialog open={showDisclaimer} onClose={handleDisclaimerCancel}>
        <DialogTitle>Note</DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            Your chat history will be stored internally to provide context-aware answers.
            Do you agree to this?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDisclaimerCancel} color="secondary">
            Cancel
          </Button>
          <Button onClick={handleDisclaimerAgree} variant="contained" color="primary">
            I Agree
          </Button>
        </DialogActions>
      </Dialog>

      <Box
        sx={{
          position: "fixed",
          bottom: 20,
          right: 20,
          zIndex: 1300,
        }}
      >
        {open ? (
          <Paper
            elevation={4}
            sx={{
              width: isLarge ? 500 : 300,
              maxHeight: isLarge ? 600 : 400,
              overflowY: "auto",
              p: 2,
              pr: 0, // Remove right padding
              display: "flex",
              flexDirection: "column",
              overflowX: "hidden",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                p: 1,
                backgroundColor: "inherit",
              }}
            >
              <Typography variant="h6">Ask GPT!</Typography>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <IconButton onClick={handleClear} size="small">
                  <DeleteIcon />
                </IconButton>
                <IconButton onClick={toggleResize} size="small">
                  <OpenInFullIcon />
                </IconButton>
                <IconButton onClick={toggleOpen} size="small">
                  <MinimizeIcon sx={{ position: "relative", top: "50%", transform: "translateY(-50%)" }} />
                </IconButton>
              </Box>
            </Box>
            <Box
              ref={chatContainerRef}
              sx={{
                flexGrow: 1,
                overflowY: "auto",
                mb: 2,
                width: '100%',
                backgroundColor: "inherit",
                "&::-webkit-scrollbar-corner": {
                  backgroundColor: "transparent",
                },
                m: 0,
                p: 0,
                pl: 1, // Added left padding only
              }}
            >
              {chatLog.map((entry, idx) => renderEntry(entry, idx))}
            </Box>
            <Box sx={{ display: "flex", p: 1 }}>
              <TextField
                fullWidth
                multiline
                minRows={1}
                variant="outlined"
                size="small"
                placeholder="Type your question..."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                onKeyDown={handleKeyDown}
              />
              <Button
                variant="contained"
                color="primary"
                onClick={handleSubmit}
                disabled={loading}
                sx={{ ml: 1 }}
              >
                Send
              </Button>
            </Box>
          </Paper>
        ) : (
          <IconButton
            onClick={toggleOpen}
            sx={{
              backgroundColor: "#10A37F",
              color: "white",
              boxShadow: 3,
              "&:hover": { backgroundColor: "#0F8B6C" },
            }}
          >
            <SmartToyIcon sx={{ fontSize: 24 }} />
          </IconButton>
        )}
      </Box>
    </>
  );
};

export default AskGPTChatbox;
